import { FiltreCustomerFilters } from '@/components/automated-scenarios/metadata/filtres/FiltreCustomerMetadata';
import { List, ListSettings } from '@/composables/GraphQL';
import { UserState } from '@/composables/User';
import getCountries from '@/configs/countries';
import { asInt, showToastError } from '@/helpers';
import { i18n } from '@/i18n';
import {
  DropdownOption, FilterOperatorsEnum, StringMap, TypeCampaignEnum,
} from '@/types';
import { ErrorConfigForm, FilterSegment } from '@/types/automated-scenarios';
import {
  NewsletterCampaigns, OperatorType, ShopsCustomersGroupsList, ShopsLists, ShopsTags,
} from '@/types/generated-types/graphql';
import { computed } from 'vue';
import { between, integer, required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { includesValuesValidator, locationGMapsValidator } from '@/helpers/CustomValidator';

let translation: any;
(async () => {
  translation = await i18n;
})();

const fetchList = async <T>(name: string, settings: ListSettings, fields: string[]): Promise<T[]> => {
  const res = await List<T>(
    {
      name,
      settings,
      fields,
    },
  );
  if (res.err) {
    showToastError('GENERIC_ERROR');
  } else {
    return res.items;
  }
  return [];
};

const fetchGroups = () => {
  const settings = {
    limit: 1000,
    offset: 0,
    order: [],
    filter: [{ field: 'id_shop', operator: OperatorType.Equals, value: UserState.activeShop ? UserState.activeShop.id : 0 }],
  };
  return fetchList<ShopsCustomersGroupsList>('ShopsCustomersGroupsList', settings, ['id_group', 'name']);
};

const fetchNewsletters = () => {
  const settings = {
    limit: 1000,
    offset: 0,
    order: [],
    filter: [
      { field: 'id_shop', operator: OperatorType.Equals, value: UserState.activeShop ? UserState.activeShop.id : 0 },
      { field: 'state', operator: OperatorType.Equals, value: 'terminated' },
    ],
  };
  return fetchList<NewsletterCampaigns>('NewsletterCampaigns', settings, ['id_newsletter_campaign', 'name']);
};

const fetchTags = (tagFilter: string) => {
  const settings = {
    limit: 1000,
    offset: 0,
    order: [],
    filter: [
      { field: 'id_shop', operator: OperatorType.Equals, value: UserState.activeShop ? UserState.activeShop.id : 0 },
      { field: 'tag', operator: OperatorType.StartsWith, value: tagFilter },
    ],
  };
  return fetchList<ShopsTags>('ShopsTags', settings, ['id_shop_tag', 'tag']);
};

const fetchLists = () => {
  const settings = {
    limit: 1000,
    offset: 0,
    order: [],
    filter: [
      { field: 'id_shop', operator: OperatorType.Equals, value: UserState.activeShop ? UserState.activeShop.id : 0 },
    ],
  };
  return fetchList<ShopsLists>('ShopsLists', settings, ['id_shop_list', 'name']);
};

export default async () => ([
  {
    id: 'filtershopdatecreation',
    availableInCampaign: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK, TypeCampaignEnum.CUSTOMER_IMPORT],
    option: { value: 'filtershopdatecreation', label: translation.global.t('automatedScenarios.fields.segments.customersSeniority') },
    fields: [
      {
        id: 'time_unit_select',
        component: 'PeriodSelector',
        data: { value: 1, unit: 'DAY', operator: FilterOperatorsEnum.EQUALS },
        props: {
          title: 'automatedScenarios.fields.customerRegisteredSince',
          units: [
            { value: 'DAY', label: translation.global.t('automatedScenarios.fields.durations.day(s)') },
            { value: 'MONTH', label: translation.global.t('automatedScenarios.fields.durations.month(s)') },
          ],
          operators: [
            { value: FilterOperatorsEnum.EQUALS, label: translation.global.t('filters.equals') },
            { value: FilterOperatorsEnum.NOT_EQUALS, label: translation.global.t('filters.notEquals') },
            { value: FilterOperatorsEnum.LESS_THAN, label: translation.global.t('filters.lessThan') },
            { value: FilterOperatorsEnum.LESS_THAN_OR_EQUAL_TO, label: translation.global.t('filters.lessThanOrEquals') },
            { value: FilterOperatorsEnum.GREATER_THAN, label: translation.global.t('filters.greaterThan') },
            { value: FilterOperatorsEnum.GREATER_THAN_OR_EQUAL_TO, label: translation.global.t('filters.greaterThanOrEquals') },
          ],
        },
        litteral_title: translation.global.t('automatedScenarios.tooltips.customerRegisteredSince'),
      },
    ],
    validate: async (data: any): Promise<ErrorConfigForm> => {
      const rules = computed(() => ({
        time_unit_select: {
          value: {
            required,
            integer,
            between: between(1, 1000),
          },
          unit: {
            required,
            includesValuesValidator: includesValuesValidator([
              'DAY',
              'MONTH',
            ]),
          },
        },
      }));

      const v$ = useVuelidate(rules, data);
      const success = await v$.value.$validate();
      return {
        success,
        validate: v$,
      };
    },
    multiple: true,
    dependencies: {
      automation: {
        operators: {
          any_parents: [
            'boxabandonpanier',
            'boxstatuscommand',
            'boxvisitpage',
            'boxsitepasstime',
            'registeronsite',
            'subscribetonewsletter',
            'boxscrollpage',
            'boxquitsite',
            'boxcustomjs',
          ],
        },
        segments: {
          any_parents_contain: [
            'filterbirthdate',
            'filtercreationdate',
            'filtercreateaccountfrom',
            'filtercreatecartfrom',
            'filterhasvouchernearexpiration',
            'filternoorderfrom',
            'filtertimesincelastorder',
          ],
        },
      },
      bulk: {},
    },
    group: 'customer',
  },
  {
    id: 'filtercreateaccountfrom',
    availableInCampaign: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK],
    option: { value: 'filtercreateaccountfrom', label: translation.global.t('automatedScenarios.fields.segments.createdAccountFor') },
    fields: [
      {
        id: 'time_unit_select',
        component: 'PeriodSelector',
        data: { value: 1, unit: 'DAY' },
        props: {
          title: 'automatedScenarios.fields.createdAccountFor',
          units: [
            { value: 'DAY', label: translation.global.t('automatedScenarios.fields.durations.day(s)') },
            { value: 'MONTH', label: translation.global.t('automatedScenarios.fields.durations.month(s)') },
          ],
        },
        litteral_title: translation.global.t('automatedScenarios.tooltips.createdAccountFor'),
      },
    ],
    validate: async (data: any): Promise<ErrorConfigForm> => {
      const rules = computed(() => ({
        time_unit_select: {
          value: {
            required,
            integer,
            between: between(1, 1000),
          },
          unit: {
            required,
            includesValuesValidator: includesValuesValidator([
              'DAY',
              'MONTH',
            ]),
          },
        },
      }));

      const v$ = useVuelidate(rules, data);
      const success = await v$.value.$validate();
      return {
        success,
        validate: v$,
      };
    },
    group: 'customer',
  },
  {
    id: 'filtercreatecartfrom',
    availableInCampaign: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK],
    option: { value: 'filtercreatecartfrom', label: translation.global.t('automatedScenarios.fields.segments.createdCartFor') },
    fields: [
      {
        id: 'time_unit_select',
        component: 'PeriodSelector',
        data: { value: 1, unit: 'DAY' },
        props: {
          title: 'automatedScenarios.fields.createdCartFor',
          units: [
            { value: 'DAY', label: translation.global.t('automatedScenarios.fields.durations.day(s)') },
            { value: 'MONTH', label: translation.global.t('automatedScenarios.fields.durations.month(s)') },
          ],
        },
        litteral_title: translation.global.t('automatedScenarios.tooltips.createdCartFor'),
      },
    ],
    validate: async (data: any): Promise<ErrorConfigForm> => {
      const rules = computed(() => ({
        time_unit_select: {
          value: {
            required,
            integer,
            between: between(1, 1000),
          },
          unit: {
            required,
            includesValuesValidator: includesValuesValidator([
              'DAY',
              'MONTH',
            ]),
          },
        },
      }));

      const v$ = useVuelidate(rules, data);
      const success = await v$.value.$validate();
      return {
        success,
        validate: v$,
      };
    },
    dependencies: {
      automation: {
        operators: {
          any_parents: [
            'boxvisitpage',
            'boxsitepasstime',
            'boxscrollpage',
            'boxquitsite',
            'boxcustomjs',
          ],
        },
      },
      bulk: {},
    },
    group: 'order',
  },
  {
    id: 'filtergender',
    availableInCampaign: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK, TypeCampaignEnum.CUSTOMER_IMPORT],
    option: { value: 'filtergender', label: translation.global.t('automatedScenarios.fields.segments.gender') },
    fields: [
      {
        id: 'gender',
        component: 'MultiDropdown',
        data: { data: [], operator: FilterOperatorsEnum.IN },
        props: {
          label: 'automatedScenarios.fields.onlyCustomers',
          options: [
            { value: 0, label: translation.global.t('automatedScenarios.fields.gender.unknown') },
            { value: 1, label: translation.global.t('automatedScenarios.fields.gender.male') },
            { value: 2, label: translation.global.t('automatedScenarios.fields.gender.female') },
          ],
          operators: [
            { value: FilterOperatorsEnum.IN, label: translation.global.t('filters.in') },
            { value: FilterOperatorsEnum.NOT_IN, label: translation.global.t('filters.notIn') },
          ],
        },
        litteral_title: translation.global.t('automatedScenarios.tooltips.onlyCustomers'),
      },
    ],
    validate: async (data: any): Promise<ErrorConfigForm> => {
      const rules = computed(() => ({
        gender: {
          required,
        },
      }));

      const v$ = useVuelidate(rules, data);
      const success = await v$.value.$validate();
      return {
        success,
        validate: v$,
      };
    },
    multiple: true,
    dependencies: {
      automation: {
        operators: {
          any_parents: [
            'boxabandonpanier',
            'boxstatuscommand',
            'boxvisitpage',
            'boxsitepasstime',
            'registeronsite',
            'subscribetonewsletter',
            'boxscrollpage',
            'boxquitsite',
            'boxcustomjs',
          ],
        },
        segments: {
          any_parents_contain: [
            'filterbirthdate',
            'filtercreationdate',
            'filtercreateaccountfrom',
            'filtercreatecartfrom',
            'filterhasvouchernearexpiration',
            'filternoorderfrom',
            'filtertimesincelastorder',
          ],
        },
      },
      bulk: {},
    },
    group: 'customer',
  },
  {
    id: 'filteragerange',
    availableInCampaign: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK, TypeCampaignEnum.CUSTOMER_IMPORT],
    option: { value: 'filteragerange', label: translation.global.t('automatedScenarios.fields.segments.ageRange') },
    fields: [
      {
        id: 'ageRange',
        component: 'NumberRange',
        data: { min: 18, max: 25, operator: FilterOperatorsEnum.BETWEEN },
        props: {
          title: 'automatedScenarios.fields.ageRange',
          min: 1,
          max: 130,
          suffix: ` ${translation.global.t('automatedScenarios.fields.yearsOld')}`,
          showLabels: false,
          operators: [
            { value: FilterOperatorsEnum.BETWEEN, label: translation.global.t('filters.between') },
            { value: FilterOperatorsEnum.NOT_BETWEEN, label: translation.global.t('filters.notBetween') },
          ],
        },
        litteral_title: translation.global.t('automatedScenarios.tooltips.ageRange'),
      },
    ],
    validate: async (data: any): Promise<ErrorConfigForm> => {
      const rules = computed(() => ({
        ageRange: {
          required,
        },
      }));

      const v$ = useVuelidate(rules, data);
      const success = await v$.value.$validate();
      return {
        success,
        validate: v$,
      };
    },
    multiple: true,
    dependencies: {
      automation: {
        operators: {
          any_parents: [
            'boxabandonpanier',
            'boxstatuscommand',
            'boxvisitpage',
            'boxsitepasstime',
            'registeronsite',
            'subscribetonewsletter',
            'boxscrollpage',
            'boxquitsite',
            'boxcustomjs',
          ],
        },
        segments: {
          any_parents_contain: [
            'filterbirthdate',
            'filtercreationdate',
            'filtercreateaccountfrom',
            'filtercreatecartfrom',
            'filterhasvouchernearexpiration',
            'filternoorderfrom',
            'filtertimesincelastorder',
          ],
        },
      },
      bulk: {},
    },
    group: 'customer',
  },
  {
    id: 'filtergroup',
    availableInCampaign: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK, TypeCampaignEnum.CUSTOMER_IMPORT],
    option: { value: 'filtergroup', label: translation.global.t('automatedScenarios.fields.segments.customerGroups') },
    fields: [
      {
        id: 'groups',
        component: 'MultiDropdown',
        data: [],
        props: {
          label: 'automatedScenarios.fields.groups',
          options: (await fetchGroups()).map((item: ShopsCustomersGroupsList) => ({ value: asInt(item.id_group), label: item.name }) as DropdownOption),
        },
        litteral_title: translation.global.t('automatedScenarios.tooltips.groups'),
      },
      {
        id: 'operator',
        component: 'ButtonGroup',
        data: 'and',
        props: {
          title: 'automatedScenarios.fields.visitorMustBeIn',
          options: [
            { value: 'and', label: translation.global.t('automatedScenarios.fields.allGroups') },
            { value: 'or', label: translation.global.t('automatedScenarios.fields.atLeastOne') },
          ],
        },
        litteral_title: translation.global.t('automatedScenarios.tooltips.visitorMustBeIn'),
      },
    ],
    validate: async (data: any): Promise<ErrorConfigForm> => {
      const rules = computed(() => ({
        groups: {
          required,
        },
        operator: {
          required,
        },
      }));

      const v$ = useVuelidate(rules, data);
      const success = await v$.value.$validate();
      return {
        success,
        validate: v$,
      };
    },
    dependencies: {
      automation: {
        operators: {
          any_parents: [
            'boxabandonpanier',
            'boxstatuscommand',
            'boxvisitpage',
            'boxsitepasstime',
            'registeronsite',
            'subscribetonewsletter',
            'boxscrollpage',
            'boxquitsite',
            'boxcustomjs',
          ],
        },
        segments: {
          any_parents_contain: [
            'filterbirthdate',
            'filtercreationdate',
            'filtercreateaccountfrom',
            'filtercreatecartfrom',
            'filterhasvouchernearexpiration',
            'filternoorderfrom',
            'filtertimesincelastorder',
          ],
        },
      },
      bulk: {},
    },
    group: 'customer',
  },
  {
    id: 'filterareas',
    availableInCampaign: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK, TypeCampaignEnum.CUSTOMER_IMPORT],
    option: { value: 'filterareas', label: translation.global.t('automatedScenarios.fields.segments.geographicArea') },
    fields: [
      {
        id: 'areas',
        component: 'Location',
        data: { 0: {} },
        props: {
          showLabels: false,
        },
        litteral_title: translation.global.t('automatedScenarios.tooltips.customerInGeographicArea'),
      },
    ],
    validate: async (data: any): Promise<ErrorConfigForm> => {
      const rules = computed(() => ({
        areas: {
          required,
          locationGMapsValidator: locationGMapsValidator(),
        },
      }));

      const v$ = useVuelidate(rules, data);
      const success = await v$.value.$validate();
      return {
        success,
        validate: v$,
      };
    },
    multiple: true,
    dependencies: {
      automation: {
        operators: {
          any_parents: [
            'boxabandonpanier',
            'boxstatuscommand',
            'boxvisitpage',
            'boxsitepasstime',
            'registeronsite',
            'subscribetonewsletter',
            'boxscrollpage',
            'boxquitsite',
            'boxcustomjs',
          ],
        },
        segments: {
          any_parents_contain: [
            'filterbirthdate',
            'filtercreationdate',
            'filtercreateaccountfrom',
            'filtercreatecartfrom',
            'filterhasvouchernearexpiration',
            'filternoorderfrom',
            'filtertimesincelastorder',
          ],
        },
      },
      bulk: {},
    },
    group: 'customer',
  },
  {
    id: 'filtercustomercountrie',
    availableInCampaign: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK],
    option: { value: 'filtercustomercountrie', label: translation.global.t('automatedScenarios.fields.segments.customersCountries') },
    fields: [
      {
        id: 'customers_countries_list',
        component: 'MultiDropdown',
        data: [],
        props: {
          label: 'automatedScenarios.fields.customersCountries',
          options: getCountries(translation.global.t),
        },
        litteral_title: translation.global.t('automatedScenarios.tooltips.customersCountries'),
      },
    ],
    validate: async (data: any): Promise<ErrorConfigForm> => {
      const rules = computed(() => ({
        customers_countries_list: {
          required,
        },
      }));

      const v$ = useVuelidate(rules, data);
      const success = await v$.value.$validate();
      return {
        success,
        validate: v$,
      };
    },
    dependencies: {
      automation: {
        operators: {
          any_parents: [
            'boxabandonpanier',
            'boxstatuscommand',
            'boxvisitpage',
            'boxsitepasstime',
            'registeronsite',
            'subscribetonewsletter',
            'boxscrollpage',
            'boxquitsite',
            'boxcustomjs',
          ],
        },
        segments: {
          any_parents_contain: [
            'filterbirthdate',
            'filtercreationdate',
            'filtercreateaccountfrom',
            'filtercreatecartfrom',
            'filterhasvouchernearexpiration',
            'filternoorderfrom',
            'filtertimesincelastorder',
          ],
        },
      },
      bulk: {},
    },
    group: 'customer',
  },
  {
    id: 'filterbirthdate',
    availableInCampaign: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK],
    option: { value: 'filterbirthdate', label: translation.global.t('automatedScenarios.fields.segments.birthday') },
    fields: [
      {
        id: 'time_unit_select',
        component: 'PeriodSelector',
        data: { value: 1, unit: 'DAY' },
        props: {
          title: 'automatedScenarios.fields.birthdayIsIn',
          units: [
            { value: 'DAY', label: translation.global.t('automatedScenarios.fields.durations.day(s)') },
            { value: 'MONTH', label: translation.global.t('automatedScenarios.fields.durations.month(s)') },
          ],
          minValue: 0,
        },
        litteral_title: translation.global.t('automatedScenarios.tooltips.birthdayIsIn'),
      },
    ],
    validate: async (data: any): Promise<ErrorConfigForm> => {
      const rules = computed(() => ({
        time_unit_select: {
          value: {
            required,
            integer,
            between: between(0, 1000),
          },
          unit: {
            required,
            includesValuesValidator: includesValuesValidator([
              'DAY',
              'MONTH',
            ]),
          },
        },
      }));

      const v$ = useVuelidate(rules, data);
      const success = await v$.value.$validate();
      return {
        success,
        validate: v$,
      };
    },
    group: 'customer',
  },
  {
    id: 'filtercreationdate',
    availableInCampaign: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK],
    option: { value: 'filtercreationdate', label: translation.global.t('automatedScenarios.fields.segments.accountCreationAnniversary') },
    fields: [
      {
        id: 'time_unit_select',
        component: 'PeriodSelector',
        data: { value: 1, unit: 'DAY' },
        props: {
          title: 'automatedScenarios.fields.accountCreationAnniversaryIsIn',
          units: [
            { value: 'DAY', label: translation.global.t('automatedScenarios.fields.durations.day(s)') },
            { value: 'MONTH', label: translation.global.t('automatedScenarios.fields.durations.month(s)') },
          ],
        },
        litteral_title: translation.global.t('automatedScenarios.tooltips.accountCreationAnniversaryIsIn'),
      },
    ],
    validate: async (data: any): Promise<ErrorConfigForm> => {
      const rules = computed(() => ({
        time_unit_select: {
          value: {
            required,
            integer,
            between: between(1, 1000),
          },
          unit: {
            required,
            includesValuesValidator: includesValuesValidator([
              'DAY',
              'MONTH',
            ]),
          },
        },
      }));

      const v$ = useVuelidate(rules, data);
      const success = await v$.value.$validate();
      return {
        success,
        validate: v$,
      };
    },
    group: 'customer',
  },
  {
    id: 'filterhasvouchernearexpiration',
    availableInCampaign: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK],
    option: { value: 'filterhasvouchernearexpiration', label: translation.global.t('automatedScenarios.fields.segments.voucher') },
    fields: [
      {
        id: 'time_unit_select',
        component: 'PeriodSelector',
        data: { value: 1, unit: 'DAY' },
        props: {
          title: 'automatedScenarios.fields.expiringVoucherIn',
          units: [
            { value: 'DAY', label: translation.global.t('automatedScenarios.fields.durations.day(s)') },
            { value: 'MONTH', label: translation.global.t('automatedScenarios.fields.durations.month(s)') },
          ],
        },
        litteral_title: translation.global.t('automatedScenarios.tooltips.expiringVoucherIn'),
      },
      {
        id: 'code_start',
        component: 'Input',
        data: '',
        props: {
          title: 'automatedScenarios.fields.voucherStartingBy',
        },
        litteral_title: translation.global.t('automatedScenarios.tooltips.voucherStartingBy'),
      },
    ],
    validate: async (data: any): Promise<ErrorConfigForm> => {
      const rules = computed(() => ({
        time_unit_select: {
          value: {
            required,
            integer,
            between: between(1, 1000),
          },
          unit: {
            required,
            includesValuesValidator: includesValuesValidator([
              'DAY',
              'MONTH',
            ]),
          },
        },
        code_start: {
        },
      }));

      const v$ = useVuelidate(rules, data);
      const success = await v$.value.$validate();
      return {
        success,
        validate: v$,
      };
    },
    group: 'order',
  },
  {
    id: 'filtercanreceivesms',
    availableInCampaign: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK],
    option: { value: 'filtercanreceivesms', label: translation.global.t('automatedScenarios.fields.segments.canReceiveSms') },
    fields: [
      {
        id: 'hidden',
        component: 'Hidden',
        data: '',
        hidden: true,
        litteral_title: '',
      },
    ],
    dependencies: {
      automation: {
        operators: {
          any_parents: [
            'boxabandonpanier',
            'boxstatuscommand',
            'boxvisitpage',
            'boxsitepasstime',
            'registeronsite',
            'subscribetonewsletter',
            'boxscrollpage',
            'boxquitsite',
            'boxcustomjs',
          ],
        },
        segments: {
          any_parents_contain: [
            'filterbirthdate',
            'filtercreationdate',
            'filtercreateaccountfrom',
            'filtercreatecartfrom',
            'filterhasvouchernearexpiration',
            'filternoorderfrom',
            'filtertimesincelastorder',
          ],
        },
      },
      bulk: {},
    },
    group: 'customer',
  },
  {
    id: 'filtercanreceivefbmessenger',
    availableInCampaign: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK],
    option: { value: 'filtercanreceivefbmessenger', label: translation.global.t('automatedScenarios.fields.segments.canReceiveFacebookMessanger') },
    fields: [
      {
        id: 'hidden',
        component: 'Hidden',
        data: '',
        hidden: true,
        litteral_title: '',
      },
    ],
    dependencies: {
      automation: {
        operators: {
          any_parents: [
            'boxabandonpanier',
            'boxstatuscommand',
            'boxvisitpage',
            'boxsitepasstime',
            'registeronsite',
            'subscribetonewsletter',
            'boxscrollpage',
            'boxquitsite',
            'boxcustomjs',
          ],
        },
        segments: {
          any_parents_contain: [
            'filterbirthdate',
            'filtercreationdate',
            'filtercreateaccountfrom',
            'filtercreatecartfrom',
            'filterhasvouchernearexpiration',
            'filternoorderfrom',
            'filtertimesincelastorder',
          ],
        },
      },
      bulk: {},
    },
    group: 'customer',
  },
  {
    id: 'filtercanreceivepushnotifications',
    availableInCampaign: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK],
    option: { value: 'filtercanreceivepushnotifications', label: translation.global.t('automatedScenarios.fields.segments.canReceivePushNotifications') },
    fields: [
      {
        id: 'hidden',
        component: 'Hidden',
        data: '',
        hidden: true,
        litteral_title: '',
      },
    ],
    dependencies: {
      automation: {
        operators: {
          any_parents: [
            'boxabandonpanier',
            'boxstatuscommand',
            'boxvisitpage',
            'boxsitepasstime',
            'registeronsite',
            'subscribetonewsletter',
            'boxscrollpage',
            'boxquitsite',
            'boxcustomjs',
          ],
        },
        segments: {
          any_parents_contain: [
            'filterbirthdate',
            'filtercreationdate',
            'filtercreateaccountfrom',
            'filtercreatecartfrom',
            'filterhasvouchernearexpiration',
            'filternoorderfrom',
            'filtertimesincelastorder',
          ],
        },
      },
      bulk: {},
    },
    group: 'customer',
  },
  {
    id: 'filtercustomersubscription',
    availableInCampaign: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK],
    option: { value: 'filtercustomersubscription', label: translation.global.t('automatedScenarios.fields.segments.hasACustomerAccount') },
    fields: [
      {
        id: 'hidden',
        component: 'Hidden',
        data: '',
        hidden: true,
        litteral_title: '',
      },
    ],
    dependencies: {
      automation: {
        operators: {
          any_parents: [
            'boxabandonpanier',
            'boxstatuscommand',
            'boxvisitpage',
            'boxsitepasstime',
            'registeronsite',
            'subscribetonewsletter',
            'boxscrollpage',
            'boxquitsite',
            'boxcustomjs',
          ],
        },
        segments: {
          any_parents_contain: [
            'filterbirthdate',
            'filtercreationdate',
            'filtercreateaccountfrom',
            'filtercreatecartfrom',
            'filterhasvouchernearexpiration',
            'filternoorderfrom',
            'filtertimesincelastorder',
          ],
        },
      },
      bulk: {},
    },
    group: 'customer',
  },
  {
    id: 'filternewslettersubscription',
    availableInCampaign: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK],
    option: { value: 'filternewslettersubscription', label: translation.global.t('automatedScenarios.fields.segments.optinType') },
    fields: [
      {
        id: 'type_optin',
        component: 'MultiDropdown',
        data: { 0: '0', 1: '1' },
        props: {
          label: 'automatedScenarios.fields.optinType',
          options: [
            { value: 0, label: 'simple' },
            { value: 1, label: 'double' },
          ],
        },
        litteral_title: translation.global.t('automatedScenarios.tooltips.optinType'),
      },
    ],
    validate: async (data: any): Promise<ErrorConfigForm> => {
      const rules = computed(() => ({
        type_optin: {
          required,
        },
      }));

      const v$ = useVuelidate(rules, data);
      const success = await v$.value.$validate();
      return {
        success,
        validate: v$,
      };
    },
    dependencies: {
      automation: {
        operators: {
          any_parents: [
            'boxabandonpanier',
            'boxstatuscommand',
            'boxvisitpage',
            'boxsitepasstime',
            'registeronsite',
            'subscribetonewsletter',
            'boxscrollpage',
            'boxquitsite',
            'boxcustomjs',
          ],
        },
        segments: {
          any_parents_contain: [
            'filterbirthdate',
            'filtercreationdate',
            'filtercreateaccountfrom',
            'filtercreatecartfrom',
            'filterhasvouchernearexpiration',
            'filternoorderfrom',
            'filtertimesincelastorder',
          ],
        },
      },
      bulk: {},
    },
    group: 'newsletter',
  },
  {
    id: 'filterhaschattedonfb',
    availableInCampaign: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK],
    option: { value: 'filterhaschattedonfb', label: translation.global.t('automatedScenarios.fields.segments.chattedOnFacebook') },
    fields: [
      {
        id: 'time_unit_select',
        component: 'PeriodSelector',
        data: { value: 1, unit: 'DAY' },
        props: {
          title: 'automatedScenarios.fields.forLessThan',
          units: [
            { value: 'DAY', label: translation.global.t('automatedScenarios.fields.durations.day(s)') },
            { value: 'MONTH', label: translation.global.t('automatedScenarios.fields.durations.month(s)') },
          ],
        },
        litteral_title: translation.global.t('automatedScenarios.tooltips.forLessThan'),
      },
    ],
    validate: async (data: any): Promise<ErrorConfigForm> => {
      const rules = computed(() => ({
        time_unit_select: {
          value: {
            required,
            integer,
            between: between(1, 1000),
          },
          unit: {
            required,
            includesValuesValidator: includesValuesValidator([
              'DAY',
              'MONTH',
            ]),
          },
        },
      }));

      const v$ = useVuelidate(rules, data);
      const success = await v$.value.$validate();
      return {
        success,
        validate: v$,
      };
    },
    dependencies: {
      automation: {
        operators: {
          any_parents: [
            'boxabandonpanier',
            'boxstatuscommand',
            'boxvisitpage',
            'boxsitepasstime',
            'registeronsite',
            'subscribetonewsletter',
            'boxscrollpage',
            'boxquitsite',
            'boxcustomjs',
          ],
        },
        segments: {
          any_parents_contain: [
            'filterbirthdate',
            'filtercreationdate',
            'filtercreateaccountfrom',
            'filtercreatecartfrom',
            'filterhasvouchernearexpiration',
            'filternoorderfrom',
            'filtertimesincelastorder',
          ],
        },
      },
      bulk: {},
    },
    group: 'social',
  },
  {
    id: 'filterhascustomertag',
    availableInCampaign: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK, TypeCampaignEnum.CUSTOMER_IMPORT],
    option: { value: 'filterhascustomertag', label: translation.global.t('automatedScenarios.fields.segments.includesTag') },
    fields: [
      {
        id: 'tags',
        component: 'EditAutocomplete',
        data: { data: [], operator: '' },
        props: {
          title: 'automatedScenarios.fields.tagList',
          multiple: true,
          field: 'tag',
          complete: (query: string) => fetchTags(query),
          operators: [
            { value: FilterOperatorsEnum.IN, label: translation.global.t('filters.in') },
            { value: FilterOperatorsEnum.NOT_IN, label: translation.global.t('filters.notIn') },
          ],
        },
        litteral_title: translation.global.t('automatedScenarios.tooltips.tagList'),
        format: (tags: StringMap) => Object.entries(tags).map((item) => ({ id_shop_tag: item[0], tag: item[1] })),
      },
      {
        id: 'operator',
        component: 'ButtonGroup',
        data: 'and',
        props: {
          title: 'automatedScenarios.fields.visitorHaving',
          options: [
            { value: 'and', label: translation.global.t('automatedScenarios.fields.allTags') },
            { value: 'or', label: translation.global.t('automatedScenarios.fields.atLeastOne') },
          ],
        },
        litteral_title: translation.global.t('automatedScenarios.tooltips.visitorHaving'),
      },
    ],
    validate: async (data: any): Promise<ErrorConfigForm> => {
      const rules = computed(() => ({
        tags: {
          required,
        },
        operator: {
          required,
        },
      }));

      const v$ = useVuelidate(rules, data);
      const success = await v$.value.$validate();
      return {
        success,
        validate: v$,
      };
    },
    dependencies: {
      automation: {
        operators: {
          any_parents: [
            'boxabandonpanier',
            'boxstatuscommand',
            'boxvisitpage',
            'boxsitepasstime',
            'registeronsite',
            'subscribetonewsletter',
            'boxscrollpage',
            'boxquitsite',
            'boxcustomjs',
          ],
        },
        segments: {
          any_parents_contain: [
            'filterbirthdate',
            'filtercreationdate',
            'filtercreateaccountfrom',
            'filtercreatecartfrom',
            'filterhasvouchernearexpiration',
            'filternoorderfrom',
            'filtertimesincelastorder',
          ],
        },
      },
      bulk: {},
    },
    group: 'customer',
  },
  {
    id: 'filtermemberoflist',
    availableInCampaign: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK],
    option: { value: 'filtermemberoflist', label: translation.global.t('automatedScenarios.fields.segments.beingPartOfAList') },
    fields: [
      {
        id: 'lists',
        component: 'MultiDropdown',
        data: [],
        props: {
          label: 'automatedScenarios.fields.lists',
          options: (await fetchLists()).map((item: ShopsLists) => ({ value: asInt(item.id_shop_list), label: item.name }) as DropdownOption),
        },
        litteral_title: translation.global.t('automatedScenarios.tooltips.lists'),
      },
      {
        id: 'operator',
        component: 'ButtonGroup',
        data: 'and',
        props: {
          title: 'automatedScenarios.fields.visitorBeingPartOf',
          options: [
            { value: 'and', label: translation.global.t('automatedScenarios.fields.allLists') },
            { value: 'or', label: translation.global.t('automatedScenarios.fields.atLeastOneList') },
          ],
        },
        litteral_title: translation.global.t('automatedScenarios.tooltips.visitorBeingPartOf'),
      },
    ],
    validate: async (data: any): Promise<ErrorConfigForm> => {
      const rules = computed(() => ({
        lists: {
          required,
        },
        operator: {
          required,
        },
      }));

      const v$ = useVuelidate(rules, data);
      const success = await v$.value.$validate();
      return {
        success,
        validate: v$,
      };
    },
    dependencies: {
      automation: {
        operators: {
          any_parents: [
            'boxabandonpanier',
            'boxstatuscommand',
            'boxvisitpage',
            'boxsitepasstime',
            'registeronsite',
            'subscribetonewsletter',
            'boxscrollpage',
            'boxquitsite',
            'boxcustomjs',
          ],
        },
        segments: {
          any_parents_contain: [
            'filterbirthdate',
            'filtercreationdate',
            'filtercreateaccountfrom',
            'filtercreatecartfrom',
            'filterhasvouchernearexpiration',
            'filternoorderfrom',
            'filtertimesincelastorder',
          ],
        },
      },
      bulk: {},
    },
  },
  {
    id: 'filterisonmobile',
    availableInCampaign: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK],
    option: { value: 'filterisonmobile', label: translation.global.t('automatedScenarios.fields.segments.useSmartphone') },
    fields: [
      {
        id: 'hidden',
        component: 'Hidden',
        data: '',
        hidden: true,
        litteral_title: '',
      },
    ],
    dependencies: {
      automation: {
        operators: {
          any_parents: ['boxvisitpage', 'boxsitepasstime', 'boxscrollpage', 'boxquitsite', 'boxcustomjs'],
        },
      },
      bulk: {},
    },
  },
]) as FilterSegment<FiltreCustomerFilters>[];
